import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

type Anbieter = 'gitlab' | 'github'
type Art = 'ok' | 'warn' | 'bad' | 'idle'

const CHIP: Record<Art, string> = {
  ok: 'bg-good-bg text-good-text',
  warn: 'bg-[#fdf1ea] text-warn',
  bad: 'bg-bad-bg text-bad',
  idle: 'bg-[#f3f2ef] text-muted',
}

function Chip({ art, children }: { art: Art; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-px text-[10.5px] font-semibold whitespace-nowrap ${CHIP[art]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  )
}

/**
 * **Umgebungen am Vorhaben** (E-443, `mockup-umgebung.html` ①).
 *
 * Repo, Coolify und Domain als bewusst gesetzte Verbindung. Die Liste kommt sofort aus Work, der
 * Stand draußen (läuft die Anwendung, ist der Name sichtbar) kommt nach.
 */
export function VorhabenUmgebungen({
  vorhabenId,
  vorhabenLabel,
  darfAendern,
}: {
  vorhabenId: string
  vorhabenLabel: string
  darfAendern: boolean
}) {
  const { data } = useQuery({
    queryKey: ['umgebung', 'liste', vorhabenId],
    queryFn: () => trpc.umgebung.liste.query({ vorhabenId }),
    staleTime: 30_000,
    retry: false,
  })
  const umgebungen = data ?? []
  const { data: live = [] } = useQuery({
    queryKey: ['umgebung', 'live', vorhabenId],
    queryFn: () => trpc.umgebung.liveStand.query({ vorhabenId }),
    enabled: umgebungen.length > 0,
    staleTime: 30_000,
    retry: false,
  })
  const [assistent, setAssistent] = useState(false)
  const [loesenFrage, setLoesenFrage] = useState<string | null>(null)
  const [anwendungFuer, setAnwendungFuer] = useState<{ id: string; label: string } | null>(null)
  const queryClient = useQueryClient()
  const neuLaden = () => void queryClient.invalidateQueries({ queryKey: ['umgebung'] })

  const automatik = useMutation({
    mutationFn: (v: { id: string; an: boolean }) => trpc.umgebung.automatik.mutate(v),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['umgebung'] }),
  })
  const loesen = useMutation({
    mutationFn: (id: string) => trpc.umgebung.loesen.mutate({ id }),
    onSuccess: () => {
      setLoesenFrage(null)
      neuLaden()
    },
  })

  if (!data) return null
  if (umgebungen.length === 0 && !darfAendern) return null

  return (
    <div className="mt-4">
      <div className="mb-1.5 flex items-baseline gap-2">
        <h4 className="text-xs font-bold">Umgebungen</h4>
        {umgebungen.length > 0 && (
          <span className="text-[11px] text-muted">{umgebungen.length}</span>
        )}
        {darfAendern && (
          <button
            type="button"
            onClick={() => setAssistent(true)}
            className="ml-auto cursor-pointer text-[11.5px] text-accent-deep hover:underline"
          >
            + Umgebung anlegen / verknüpfen
          </button>
        )}
      </div>

      {umgebungen.length === 0 && (
        <p className="text-[12.5px] text-muted">
          Keine. Eine Umgebung verbindet ein Repo, ein Coolify-Projekt und eine Domain.
        </p>
      )}

      <div className="grid gap-2">
        {umgebungen.map((u) => {
          const stand = live.find((l) => l.id === u.id)
          return (
            <div key={u.id} className="overflow-hidden rounded-xl border border-line">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line bg-[#f7f7f5] px-3 py-2">
                <span className="text-[13px] font-bold">{u.label}</span>
                {stand?.app && <Chip art={stand.app.art}>{stand.app.text}</Chip>}
                <div className="ml-auto flex flex-wrap items-center gap-2 text-[11.5px]">
                  {darfAendern && u.repoPfad && u.coolifyProjektName && !u.coolifyAppName && (
                    <button
                      type="button"
                      className="cursor-pointer rounded-lg bg-accent px-2.5 py-0.5 font-semibold text-white hover:brightness-105"
                      onClick={() => setAnwendungFuer({ id: u.id, label: u.label })}
                    >
                      Anwendung anlegen
                    </button>
                  )}
                  {u.fqdn && (
                    <a
                      href={`https://${u.fqdn}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent-deep hover:underline"
                    >
                      Öffnen ↗
                    </a>
                  )}
                  {darfAendern &&
                    (loesenFrage === u.id ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-muted">Lösen? Coolify und DNS bleiben.</span>
                        <button
                          type="button"
                          className="cursor-pointer font-semibold text-bad hover:underline"
                          onClick={() => loesen.mutate(u.id)}
                        >
                          lösen
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer text-muted hover:underline"
                          onClick={() => setLoesenFrage(null)}
                        >
                          abbrechen
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="cursor-pointer text-muted hover:underline"
                        onClick={() => setLoesenFrage(u.id)}
                      >
                        Verknüpfung lösen
                      </button>
                    ))}
                </div>
              </div>
              <div className="text-[12.5px]">
                <Zeile titel={u.gitAnbieter === 'github' ? 'GitHub' : 'GitLab'} leer={!u.repoPfad}>
                  {u.repoPfad && (
                    <>
                      {u.repoUrl ? (
                        <a
                          href={u.repoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono hover:underline"
                        >
                          {u.repoPfad}
                        </a>
                      ) : (
                        <span className="font-mono">{u.repoPfad}</span>
                      )}{' '}
                      <span className="text-muted">· {u.branch}</span>
                      {u.repoUrl && <KlonZeile repoUrl={u.repoUrl} pfad={u.repoPfad} />}
                    </>
                  )}
                </Zeile>
                <Zeile titel="Coolify" leer={!u.coolifyProjektName}>
                  {u.coolifyProjektName}
                  <span className="text-muted">
                    {' '}
                    › {u.coolifyUmgebung}
                    {u.coolifyAppName
                      ? ` · Anwendung ${u.coolifyAppName}`
                      : ' · noch keine Anwendung'}
                  </span>
                  {!u.coolifyAppName && u.repoPfad && u.coolifyProjektName && (
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11.5px]">
                      {darfAendern ? (
                        <label className="flex cursor-pointer items-center gap-1 text-ink2">
                          <input
                            type="checkbox"
                            checked={u.automatisch}
                            onChange={(e) => automatik.mutate({ id: u.id, an: e.target.checked })}
                          />
                          von selbst anlegen, sobald Code im Repo liegt
                        </label>
                      ) : (
                        u.automatisch && <span className="text-ink2">⟳ legt von selbst an</span>
                      )}
                      {u.hinweis && <span className="text-muted">· {u.hinweis}</span>}
                    </span>
                  )}
                  {u.coolifyAppName && u.hinweis && (
                    <span className="mt-1 block text-[11.5px] text-muted">{u.hinweis}</span>
                  )}
                </Zeile>
                <Zeile
                  titel="Domain"
                  leer={!u.fqdn}
                  rechts={stand?.dns && <Chip art={stand.dns.art}>{stand.dns.text}</Chip>}
                >
                  {u.fqdn && (
                    <>
                      <span className="font-mono">{u.fqdn}</span>{' '}
                      <span className="text-muted">
                        → A {u.zielIp}
                        {u.zielLabel ? ` (${u.zielLabel})` : ''}
                      </span>
                    </>
                  )}
                </Zeile>
              </div>
            </div>
          )
        })}
      </div>

      {anwendungFuer && (
        <AnwendungDialog
          umgebungId={anwendungFuer.id}
          label={anwendungFuer.label}
          schliessen={() => {
            setAnwendungFuer(null)
            neuLaden()
          }}
        />
      )}

      {assistent && (
        <UmgebungAssistent
          vorhabenId={vorhabenId}
          vorhabenLabel={vorhabenLabel}
          schliessen={() => {
            setAssistent(false)
            neuLaden()
          }}
        />
      )}
    </div>
  )
}

function Zeile({
  titel,
  leer,
  rechts,
  children,
}: {
  titel: string
  leer: boolean
  rechts?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-3 border-t border-line px-3 py-1.5 first:border-t-0">
      <span className="w-[62px] shrink-0 text-[11px] text-muted">{titel}</span>
      <span className="min-w-0 flex-1 break-words">
        {leer ? <span className="text-muted">–</span> : children}
      </span>
      {rechts}
    </div>
  )
}

/* ── Assistent ─────────────────────────────────────────────────────────── */

interface RepoWahl {
  anbieter: Anbieter
  id: string
  pfad: string
  webUrl: string
  standardBranch: string
}

const STUFEN = ['Repository', 'Coolify', 'Domain', 'Prüfen & anlegen'] as const

const NAME_FARBE: Record<string, Art> = {
  frei: 'ok',
  vorhanden: 'ok',
  fehler: 'warn',
  ungueltig: 'bad',
  'nicht-freigegeben': 'bad',
  gesperrt: 'bad',
  vergeben: 'bad',
  belegt: 'bad',
}

function UmgebungAssistent({
  vorhabenId,
  vorhabenLabel,
  schliessen,
}: {
  vorhabenId: string
  vorhabenLabel: string
  schliessen: () => void
}) {
  const [stufe, setStufe] = useState(0)
  const [label, setLabel] = useState('Produktion')

  /* Stufe 1 – Repository */
  const [anbieter, setAnbieter] = useState<Anbieter>('github')
  const [ohneRepo, setOhneRepo] = useState(false)
  /** Neues Repo statt eines vorhandenen – angelegt wird es erst im letzten Schritt. */
  const [neuRepo, setNeuRepo] = useState(false)
  const [ortId, setOrtId] = useState('')
  const [repoName, setRepoName] = useState(() =>
    vorhabenLabel
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, ''),
  )
  const { data: orteDaten } = useQuery({
    queryKey: ['umgebung', 'repoOrte', anbieter],
    queryFn: () => trpc.umgebung.repoOrte.query({ anbieter }),
    enabled: neuRepo,
    staleTime: 120_000,
    retry: false,
  })
  const orte = orteDaten?.orte ?? []
  const ort = orte.find((o) => o.id === ortId) ?? orte[0] ?? null
  const repoNameOk = /^[a-z0-9][a-z0-9._-]{0,98}[a-z0-9]$/.test(repoName)
  const [suche, setSuche] = useState('')
  const [repo, setRepo] = useState<RepoWahl | null>(null)
  const [branch, setBranch] = useState('')
  const { data: repoDaten } = useQuery({
    queryKey: ['umgebung', 'repos', anbieter],
    queryFn: () => trpc.umgebung.repos.query({ anbieter }),
    staleTime: 120_000,
    retry: false,
  })
  const { data: branchListe = [] } = useQuery({
    queryKey: ['umgebung', 'branches', repo?.anbieter, repo?.id],
    queryFn: () =>
      trpc.umgebung.branches.query({
        anbieter: repo!.anbieter,
        repoId: repo!.id,
        repoPfad: repo!.pfad,
      }),
    enabled: Boolean(repo),
    staleTime: 120_000,
    retry: false,
  })
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase()
    return (repoDaten?.repos ?? [])
      .filter((r) => !q || r.pfad.toLowerCase().includes(q))
      .slice(0, 80)
  }, [repoDaten, suche])
  const gibtsSchon =
    neuRepo && ort
      ? (repoDaten?.repos ?? []).find((r) => r.pfad === `${ort.pfad}/${repoName}`)
      : undefined
  const kurzname = neuRepo ? repoName : repo ? (repo.pfad.split('/').pop() ?? '').toLowerCase() : ''

  /* Stufe 2 – Coolify */
  const { data: coolify } = useQuery({
    queryKey: ['umgebung', 'coolify'],
    queryFn: () => trpc.umgebung.coolify.query(),
    staleTime: 60_000,
    retry: false,
  })
  const [cfModus, setCfModus] = useState<'verknuepfen' | 'neu' | 'ohne'>('verknuepfen')
  /** Anwendung von selbst anlegen, sobald Code im Repo liegt – Vorgabe an. */
  const [automatisch, setAutomatisch] = useState(true)
  const [projektUuid, setProjektUuid] = useState('')
  const [projektName, setProjektName] = useState('')
  const [cfUmgebung, setCfUmgebung] = useState('production')
  const [appUuid, setAppUuid] = useState('')
  const projekt = coolify?.projekte.find((p) => p.uuid === projektUuid) ?? null
  const umgebungImProjekt = projekt?.umgebungen.find((u) => u.name === cfUmgebung) ?? null
  const passendeApp = (a: { repoPfad: string; branch: string }) =>
    Boolean(repo) && a.repoPfad === repo!.pfad.toLowerCase()

  /** Beim Betreten von Stufe 2: das passende Projekt vorschlagen (Anwendung mit diesem Repo, sonst gleicher Name). */
  const coolifyVorschlagen = () => {
    if (!coolify) return
    const mitApp = coolify.projekte.find((p) =>
      p.umgebungen.some((u) => u.anwendungen.some(passendeApp)),
    )
    /** „NetCon Evidence" passt zu `netconevidence` – verglichen wird ohne Leer- und Sonderzeichen. */
    const flach = (t: string) => t.toLowerCase().replace(/[^a-z0-9]/g, '')
    const gleich = coolify.projekte.find((p) => flach(p.name) === flach(kurzname))
    const treffer = mitApp ?? gleich
    if (treffer) {
      setCfModus('verknuepfen')
      setProjektUuid(treffer.uuid)
      const u =
        treffer.umgebungen.find((x) => x.anwendungen.some(passendeApp)) ?? treffer.umgebungen[0]
      if (u) setCfUmgebung(u.name)
      const app =
        u?.anwendungen.find((a) => passendeApp(a) && a.branch === branch) ??
        u?.anwendungen.find(passendeApp)
      setAppUuid(app?.uuid ?? '')
    } else if (!projektUuid) {
      setCfModus(coolify.projekte.length ? 'neu' : 'neu')
      setProjektName(kurzname || vorhabenLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
    }
  }

  /* Stufe 3 – Domain */
  const { data: zonen = [] } = useQuery({
    queryKey: ['umgebung', 'zonenAuswahl'],
    queryFn: () => trpc.umgebung.zonenAuswahl.query(),
    staleTime: 60_000,
    retry: false,
  })
  const [mitDomain, setMitDomain] = useState(true)
  const [sub, setSub] = useState('')
  const [suffix, setSuffix] = useState('')
  const [ziel, setZiel] = useState('')
  const [eigeneIp, setEigeneIp] = useState('')
  useEffect(() => {
    if (!suffix && zonen[0]) setSuffix(zonen[0].suffix)
  }, [zonen, suffix])
  const zone = zonen.find((z) => z.suffix === suffix)
  /** Nur echte IPv4-Ziele – Coolify listet auch „localhost · host.docker.internal". */
  const server = (coolify?.server ?? []).filter((s) => /^\d{1,3}(\.\d{1,3}){3}$/.test(s.ip))
  const zielIp =
    ziel === 'eigen'
      ? eigeneIp.trim()
      : (server.find((s) => s.uuid === ziel)?.ip ?? (ziel === '' ? (zone?.zielIp ?? '') : ''))
  const zielLabel =
    ziel === 'eigen'
      ? 'eigene IP'
      : (server.find((s) => s.uuid === ziel)?.name ??
        (ziel === '' ? (zone?.zielLabel ?? null) : null))
  const fqdn = sub.trim() && suffix ? `${sub.trim().toLowerCase()}.${suffix}` : ''

  const [geprueft, setGeprueft] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setGeprueft(`${fqdn}|${zielIp}`), 400)
    return () => clearTimeout(t)
  }, [fqdn, zielIp])
  const namensStand = useQuery({
    queryKey: ['umgebung', 'name', geprueft, appUuid],
    queryFn: () =>
      trpc.umgebung.namePruefen.query({
        name: fqdn,
        ip: zielIp,
        coolifyAppUuid: cfModus === 'verknuepfen' && appUuid ? appUuid : null,
      }),
    enabled: mitDomain && Boolean(fqdn) && Boolean(zielIp) && geprueft === `${fqdn}|${zielIp}`,
    staleTime: 0,
    retry: false,
  })
  const domainOk =
    !mitDomain || namensStand.data?.art === 'frei' || namensStand.data?.art === 'vorhanden'

  /* Stufe 4 – Ausführen */
  const anlegen = useMutation({
    mutationFn: () =>
      trpc.umgebung.anlegen.mutate({
        vorhabenId,
        label: label.trim(),
        neuesRepo:
          !ohneRepo && neuRepo && ort
            ? {
                anbieter,
                ortId: ort.id,
                ortPfad: ort.pfad,
                name: repoName,
                beschreibung: vorhabenLabel,
              }
            : null,
        git:
          ohneRepo || neuRepo || !repo
            ? null
            : {
                anbieter: repo.anbieter,
                repoId: repo.id,
                repoPfad: repo.pfad,
                repoUrl: repo.webUrl,
                branch: branch || repo.standardBranch,
              },
        coolify:
          cfModus === 'ohne'
            ? null
            : cfModus === 'neu'
              ? { modus: 'neu', projektName: projektName.trim(), umgebung: cfUmgebung.trim() }
              : {
                  modus: 'verknuepfen',
                  projektUuid,
                  umgebung: cfUmgebung.trim(),
                  appUuid: appUuid || null,
                },
        domain: mitDomain ? { name: fqdn, ip: zielIp, zielLabel } : null,
        automatisch: automatischMoeglich && automatisch,
      }),
  })

  /* Esc schließt nur den Assistenten, nicht das Vorhaben darunter. */
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      if (!anlegen.isPending) schliessen()
    }
    window.addEventListener('keydown', taste, true)
    return () => window.removeEventListener('keydown', taste, true)
  }, [schliessen, anlegen.isPending])

  const stufeOk = [
    label.trim().length > 0 && (ohneRepo || (neuRepo ? repoNameOk && Boolean(ort) : Boolean(repo))),
    cfModus === 'ohne' ||
      ((cfModus === 'neu' ? projektName.trim().length >= 2 : Boolean(projektUuid)) &&
        cfUmgebung.trim().length > 0),
    domainOk,
    true,
  ]

  const weiter = () => {
    if (stufe === 0) coolifyVorschlagen()
    setStufe((s) => Math.min(3, s + 1))
  }

  /** Nur sinnvoll mit Repo und Coolify-Projekt und solange keine Anwendung verknüpft ist. */
  const automatischMoeglich =
    !ohneRepo && (neuRepo || Boolean(repo)) && cfModus !== 'ohne' && !(cfModus === 'verknuepfen' && appUuid)
  const plan: { bereich: string; was: string; verb: 'anlegen' | 'verknüpfen' | 'vorhanden' }[] = []
  if (!ohneRepo && neuRepo && ort)
    plan.push({
      bereich: 'Repo',
      was: `${ort.pfad}/${repoName} (privat, mit README, main)`,
      verb: gibtsSchon ? 'verknüpfen' : 'anlegen',
    })
  if (!ohneRepo && !neuRepo && repo)
    plan.push({
      bereich: 'Repo',
      was: `${repo.pfad} @ ${branch || repo.standardBranch}`,
      verb: 'verknüpfen',
    })
  if (cfModus === 'neu') {
    const gibts = coolify?.projekte.find(
      (p) => p.name.toLowerCase() === projektName.trim().toLowerCase(),
    )
    plan.push({
      bereich: 'Coolify',
      was: `Projekt ${projektName.trim()}`,
      verb: gibts ? 'verknüpfen' : 'anlegen',
    })
    plan.push({
      bereich: '',
      was: `Umgebung ${cfUmgebung}`,
      verb: cfUmgebung === 'production' ? 'vorhanden' : 'anlegen',
    })
  } else if (cfModus === 'verknuepfen' && projekt) {
    plan.push({ bereich: 'Coolify', was: `Projekt ${projekt.name}`, verb: 'verknüpfen' })
    plan.push({
      bereich: '',
      was: `Umgebung ${cfUmgebung}`,
      verb: umgebungImProjekt ? 'vorhanden' : 'anlegen',
    })
    const app = umgebungImProjekt?.anwendungen.find((a) => a.uuid === appUuid)
    if (app) plan.push({ bereich: '', was: `Anwendung ${app.name}`, verb: 'verknüpfen' })
  }
  if (mitDomain && fqdn)
    plan.push({
      bereich: 'DNS',
      was: `${fqdn} → A ${zielIp}${zielLabel ? ` (${zielLabel})` : ''}`,
      verb: namensStand.data?.art === 'vorhanden' ? 'verknüpfen' : 'anlegen',
    })
  if (automatischMoeglich && automatisch)
    plan.push({
      bereich: '',
      was: 'Anwendung – von selbst, sobald Code im Repo liegt (Work schaut alle 5 Minuten)',
      verb: 'anlegen',
    })
  plan.push({ bereich: 'Work', was: `Umgebung „${label.trim()}“ am Vorhaben`, verb: 'anlegen' })

  const ergebnis = anlegen.data

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[5vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen, !anlegen.isPending)}
      role="dialog"
      aria-modal="true"
      aria-label="Umgebung anlegen oder verknüpfen"
    >
      <div className="flex max-h-[min(88vh,100%)] w-full max-w-[720px] min-w-0 flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-start gap-2 border-b border-line px-[18px] py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Umgebung für „{vorhabenLabel}“</h3>
            <p className="text-[11.5px] text-muted">
              Es wird erst etwas angelegt, wenn du im letzten Schritt bestätigst.
            </p>
          </div>
          <button
            type="button"
            onClick={schliessen}
            disabled={anlegen.isPending}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <nav
          className="flex gap-1 overflow-x-auto border-b border-line px-[18px] py-2"
          aria-label="Schritte"
        >
          {STUFEN.map((t, i) => (
            <button
              key={t}
              type="button"
              disabled={
                anlegen.isPending ||
                Boolean(ergebnis) ||
                (i > stufe && !stufeOk.slice(0, i).every(Boolean))
              }
              onClick={() => {
                if (i > stufe && stufe === 0) coolifyVorschlagen()
                setStufe(i)
              }}
              className={`flex cursor-pointer items-center gap-1.5 rounded-full py-1 pr-2.5 pl-1 text-[12px] whitespace-nowrap ${
                i === stufe ? 'bg-accent-soft font-semibold text-accent-deep' : 'text-muted'
              }`}
            >
              <span
                className={`grid h-5 w-5 place-items-center rounded-full text-[10.5px] font-bold ${
                  i === stufe
                    ? 'bg-accent text-white'
                    : i < stufe
                      ? 'bg-good-bg text-good-text'
                      : 'bg-[#f3f2ef]'
                }`}
              >
                {i < stufe ? '✓' : i + 1}
              </span>
              {t}
            </button>
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5 text-[13px]">
          {stufe === 0 && (
            <div className="grid gap-3">
              <label className="grid gap-1">
                <span className="text-[12px] font-semibold text-ink2">Name der Umgebung</span>
                <input
                  className="rounded-lg border border-line2 px-2.5 py-1.5"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Portal · Produktion"
                />
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-[10px] bg-[#f3f2ef] p-[3px]">
                  {(['github', 'gitlab'] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      aria-pressed={anbieter === a}
                      disabled={ohneRepo}
                      onClick={() => {
                        setAnbieter(a)
                        setRepo(null)
                        setBranch('')
                      }}
                      className={`cursor-pointer rounded-lg px-3 py-1 text-[12.5px] ${
                        anbieter === a
                          ? 'bg-surface font-semibold shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                          : 'text-ink2'
                      }`}
                    >
                      {a === 'github' ? 'GitHub' : 'GitLab'}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-1.5 text-[12px] text-ink2">
                  <input
                    type="checkbox"
                    checked={ohneRepo}
                    onChange={(e) => setOhneRepo(e.target.checked)}
                  />
                  ohne Repository
                </label>
                {!ohneRepo && (
                  <label className="flex items-center gap-1.5 text-[12px] text-ink2">
                    <input
                      type="checkbox"
                      checked={neuRepo}
                      onChange={(e) => setNeuRepo(e.target.checked)}
                    />
                    neues Repository anlegen
                  </label>
                )}
              </div>
              {!ohneRepo && neuRepo && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Wohin</span>
                    <select
                      className="rounded-lg border border-line2 px-2 py-1.5 font-mono text-[12.5px]"
                      value={ort?.id ?? ''}
                      onChange={(e) => setOrtId(e.target.value)}
                    >
                      {orte.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.pfad}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Name</span>
                    <input
                      className="rounded-lg border border-line2 px-2.5 py-1.5 font-mono"
                      value={repoName}
                      onChange={(e) => setRepoName(e.target.value.toLowerCase())}
                      spellCheck={false}
                    />
                  </label>
                  {orteDaten?.fehler && (
                    <p className="text-[12px] text-bad sm:col-span-2">✗ {orteDaten.fehler}</p>
                  )}
                  <p
                    className={`text-[11.5px] sm:col-span-2 ${!repoNameOk ? 'text-bad' : gibtsSchon ? 'text-good-text' : 'text-muted'}`}
                  >
                    {!repoNameOk
                      ? 'Name: a–z, 0–9, - _ . (mindestens 2 Zeichen)'
                      : gibtsSchon
                        ? `✓ ${ort?.pfad}/${repoName} gibt es schon – Work verknüpft es statt es doppelt anzulegen.`
                        : `Wird im letzten Schritt angelegt: ${ort?.pfad ?? '…'}/${repoName}, privat, mit README, Branch main.`}
                  </p>
                </div>
              )}
              {!ohneRepo && !neuRepo && (
                <>
                  <input
                    className="rounded-lg border border-line2 px-2.5 py-1.5"
                    placeholder="Repository suchen …"
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                  />
                  {repoDaten?.fehler && (
                    <p className="text-[12px] text-bad">✗ {repoDaten.fehler}</p>
                  )}
                  <div
                    className="max-h-[240px] overflow-y-auto rounded-[11px] border border-line"
                    role="radiogroup"
                  >
                    {!repoDaten && <p className="px-3 py-2 text-[12px] text-muted">lädt …</p>}
                    {repoDaten && gefiltert.length === 0 && !repoDaten.fehler && (
                      <p className="px-3 py-2 text-[12px] text-muted">Kein Repository gefunden.</p>
                    )}
                    {gefiltert.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        role="radio"
                        aria-checked={repo?.id === r.id}
                        onClick={() => {
                          setRepo(r)
                          setBranch(r.standardBranch)
                        }}
                        className={`flex w-full cursor-pointer items-center gap-2.5 border-t border-line px-3 py-1.5 text-left first:border-t-0 hover:bg-[#f7f7f5] ${
                          repo?.id === r.id ? 'bg-accent-soft' : ''
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">
                          {r.pfad}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted">
                          {r.privat ? 'privat · ' : ''}
                          {r.zuletzt ? new Date(r.zuletzt).toLocaleDateString('de-DE') : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                  {repo && (
                    <label className="grid gap-1">
                      <span className="text-[12px] font-semibold text-ink2">Branch</span>
                      <select
                        className="rounded-lg border border-line2 px-2 py-1.5"
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                      >
                        {[...new Set([repo.standardBranch, ...branchListe])].map((b) => (
                          <option key={b}>{b}</option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              )}
            </div>
          )}

          {stufe === 1 && (
            <div className="grid gap-3">
              {coolify?.fehler && (
                <p className="text-[12px] text-bad">✗ Coolify: {coolify.fehler}</p>
              )}
              {!coolify && <p className="text-[12px] text-muted">Coolify wird abgefragt …</p>}
              <div className="grid gap-2" role="radiogroup">
                {(
                  [
                    [
                      'verknuepfen',
                      'Vorhandenes Projekt verknüpfen',
                      'Projekt, Umgebung und – falls schon da – die Anwendung.',
                    ],
                    [
                      'neu',
                      'Neues Projekt in Coolify anlegen',
                      'Gibt es ein Projekt mit dem Namen schon, wird es verknüpft.',
                    ],
                    ['ohne', 'Ohne Coolify', 'Nur Repo und/oder Domain.'],
                  ] as const
                ).map(([m, titel, text]) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={cfModus === m}
                    onClick={() => setCfModus(m)}
                    className={`cursor-pointer rounded-[11px] border px-3 py-2 text-left ${
                      cfModus === m ? 'border-accent bg-accent-soft' : 'border-line2'
                    }`}
                  >
                    <b className="block text-[13px]">{titel}</b>
                    <span className="text-[12px] text-ink2">{text}</span>
                  </button>
                ))}
              </div>

              {cfModus === 'verknuepfen' && coolify && (
                <div className="grid gap-2 sm:grid-cols-3">
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Projekt</span>
                    <select
                      className="rounded-lg border border-line2 px-2 py-1.5"
                      value={projektUuid}
                      onChange={(e) => {
                        setProjektUuid(e.target.value)
                        setAppUuid('')
                        const p = coolify.projekte.find((x) => x.uuid === e.target.value)
                        if (p?.umgebungen[0]) setCfUmgebung(p.umgebungen[0].name)
                      }}
                    >
                      <option value="">– wählen –</option>
                      {coolify.projekte.map((p) => (
                        <option key={p.uuid} value={p.uuid}>
                          {p.name}
                          {p.umgebungen.some((u) => u.anwendungen.some(passendeApp))
                            ? ' ★ passt zum Repo'
                            : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Umgebung</span>
                    <input
                      list="umgebung-cf-umgebungen"
                      className="rounded-lg border border-line2 px-2 py-1.5"
                      value={cfUmgebung}
                      onChange={(e) => {
                        setCfUmgebung(e.target.value)
                        setAppUuid('')
                      }}
                    />
                    <datalist id="umgebung-cf-umgebungen">
                      {projekt?.umgebungen.map((u) => (
                        <option key={u.name} value={u.name} />
                      ))}
                    </datalist>
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Anwendung</span>
                    <select
                      className="rounded-lg border border-line2 px-2 py-1.5"
                      value={appUuid}
                      onChange={(e) => setAppUuid(e.target.value)}
                      disabled={!umgebungImProjekt}
                    >
                      <option value="">keine verknüpfen</option>
                      {umgebungImProjekt?.anwendungen.map((a) => (
                        <option key={a.uuid} value={a.uuid}>
                          {a.name}
                          {passendeApp(a) ? ` ★ ${a.branch}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {projekt && !umgebungImProjekt && cfUmgebung.trim() && (
                    <p className="text-[11.5px] text-accent-deep sm:col-span-3">
                      Die Umgebung „{cfUmgebung.trim()}“ gibt es in {projekt.name} noch nicht – Work
                      legt sie an.
                    </p>
                  )}
                </div>
              )}

              {cfModus === 'neu' && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Projektname</span>
                    <input
                      className="rounded-lg border border-line2 px-2 py-1.5"
                      value={projektName}
                      onChange={(e) => setProjektName(e.target.value)}
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Umgebung</span>
                    <input
                      className="rounded-lg border border-line2 px-2 py-1.5"
                      value={cfUmgebung}
                      onChange={(e) => setCfUmgebung(e.target.value)}
                    />
                  </label>
                  {coolify?.projekte.some(
                    (p) => p.name.toLowerCase() === projektName.trim().toLowerCase(),
                  ) && (
                    <p className="text-[11.5px] text-good-text sm:col-span-2">
                      ✓ „{projektName.trim()}“ gibt es in Coolify schon – Work verknüpft es statt es
                      doppelt anzulegen.
                    </p>
                  )}
                </div>
              )}

              {automatischMoeglich && (
                <label className="flex items-start gap-2 rounded-lg bg-[#f7f7f5] px-3 py-2 text-[12px] text-ink2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={automatisch}
                    onChange={(e) => setAutomatisch(e.target.checked)}
                  />
                  <span>
                    <b>Anwendung von selbst anlegen, sobald Code im Repo liegt.</b> Work schaut alle 5
                    Minuten nach, erkennt Build-Art, Port und Variablen und rollt aus. Ist es sich nicht
                    sicher oder fehlt ein Wert (z. B. ein API-Schlüssel), meldet es sich an der Umgebung.
                  </span>
                </label>
              )}
              {cfModus !== 'ohne' && !automatischMoeglich && (
                <p className="rounded-lg bg-[#f7f7f5] px-3 py-2 text-[11.5px] text-ink2">
                  Gibt es noch keine Anwendung, legst du sie danach an der Umgebung mit{' '}
                  <b>„Anwendung anlegen“</b> an – Work erkennt Build-Art und Port selbst.
                </p>
              )}
            </div>
          )}

          {stufe === 2 && (
            <div className="grid gap-3">
              <label className="flex items-center gap-1.5 text-[12.5px]">
                <input
                  type="checkbox"
                  checked={mitDomain}
                  onChange={(e) => setMitDomain(e.target.checked)}
                />
                Mit Domain <span className="text-muted">(ohne: nur intern erreichbar)</span>
              </label>
              {mitDomain && zonen.length === 0 && (
                <p className="text-[12px] text-bad">
                  Es ist keine Zone freigegeben – ein Admin gibt sie unter Einstellungen ›
                  Umgebungen frei.
                </p>
              )}
              {mitDomain && zonen.length > 0 && (
                <>
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Adresse</span>
                    <span className="flex overflow-hidden rounded-lg border border-line2">
                      <input
                        className="min-w-0 flex-1 px-2.5 py-1.5 font-mono"
                        value={sub}
                        onChange={(e) => setSub(e.target.value)}
                        placeholder={kurzname || 'name'}
                        spellCheck={false}
                        autoComplete="off"
                      />
                      <select
                        className="border-l border-line2 bg-[#f7f7f5] px-2 font-mono text-[12.5px]"
                        value={suffix}
                        onChange={(e) => setSuffix(e.target.value)}
                        aria-label="Zone"
                      >
                        {zonen.map((z) => (
                          <option key={z.suffix} value={z.suffix}>
                            .{z.suffix}
                          </option>
                        ))}
                      </select>
                    </span>
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="grid gap-1">
                      <span className="text-[12px] font-semibold text-ink2">Zeigt auf</span>
                      <select
                        className="rounded-lg border border-line2 px-2 py-1.5"
                        value={ziel}
                        onChange={(e) => setZiel(e.target.value)}
                      >
                        <option value="">
                          {zone?.zielIp
                            ? `Vorgabe der Zone: ${zone.zielLabel ?? ''} ${zone.zielIp}`
                            : '– Ziel wählen –'}
                        </option>
                        {server.map((s) => (
                          <option key={s.uuid} value={s.uuid}>
                            Coolify: {s.name} · {s.ip}
                          </option>
                        ))}
                        <option value="eigen">eigene IP …</option>
                      </select>
                    </label>
                    {ziel === 'eigen' && (
                      <label className="grid gap-1">
                        <span className="text-[12px] font-semibold text-ink2">IPv4</span>
                        <input
                          className="rounded-lg border border-line2 px-2 py-1.5 font-mono"
                          value={eigeneIp}
                          onChange={(e) => setEigeneIp(e.target.value)}
                          placeholder="203.0.113.10"
                        />
                      </label>
                    )}
                  </div>
                  {fqdn && zielIp && (
                    <div
                      className={`rounded-[10px] px-3 py-2 text-[12.5px] ${
                        namensStand.data
                          ? CHIP[NAME_FARBE[namensStand.data.art] ?? 'idle']
                          : CHIP.idle
                      }`}
                    >
                      {namensStand.isFetching || !namensStand.data
                        ? `${fqdn} wird geprüft …`
                        : `${namensStand.data.art === 'frei' || namensStand.data.art === 'vorhanden' ? '✓' : '✕'} ${namensStand.data.text}`}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {stufe === 3 && (
            <div className="grid gap-3">
              {!ergebnis && (
                <>
                  <p className="text-[12.5px] text-ink2">
                    Das passiert, wenn du bestätigst. Bricht ein Schritt ab, bleibt das Angelegte
                    stehen – ein zweiter Versuch findet es und verknüpft es nur noch.
                  </p>
                  <div className="overflow-hidden rounded-[11px] border border-line">
                    {plan.map((p, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 border-t border-line px-3 py-2 first:border-t-0"
                      >
                        <span className="w-[54px] shrink-0 text-[11px] text-muted">
                          {p.bereich}
                        </span>
                        <span className="min-w-0 flex-1 break-words">{p.was}</span>
                        <span
                          className={`rounded-md px-1.5 py-px text-[10px] font-bold tracking-wide uppercase ${
                            p.verb === 'anlegen'
                              ? 'bg-accent-soft text-accent-deep'
                              : p.verb === 'verknüpfen'
                                ? 'bg-vorhaben-bg text-pool-projekte'
                                : 'bg-[#f3f2ef] text-muted'
                          }`}
                        >
                          {p.verb}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {anlegen.error && <p className="text-[12px] text-bad">✗ {anlegen.error.message}</p>}
              {ergebnis && (
                <>
                  <div className="overflow-hidden rounded-[11px] border border-line">
                    {ergebnis.schritte.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 border-t border-line px-3 py-2 first:border-t-0"
                      >
                        <span className={s.ergebnis === 'fehler' ? 'text-bad' : 'text-good-text'}>
                          {s.ergebnis === 'fehler' ? '✕' : '✓'}
                        </span>
                        <span className="min-w-0 flex-1 break-words">
                          {s.was}
                          {s.text && (
                            <span className="block text-[11.5px] text-muted">{s.text}</span>
                          )}
                        </span>
                        <span className="text-[11px] text-muted">{s.ergebnis}</span>
                      </div>
                    ))}
                  </div>
                  <p className={`text-[12.5px] ${ergebnis.ok ? 'text-good-text' : 'text-bad'}`}>
                    {ergebnis.ok
                      ? `„${label.trim()}“ hängt jetzt am Vorhaben.${mitDomain ? ' Ein neuer DNS-Name ist meist nach wenigen Minuten sichtbar.' : ''}`
                      : 'Abgebrochen. Was schon angelegt ist, bleibt – nach dem Beheben einfach noch einmal ausführen.'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-line px-[18px] py-3">
          <span className="mr-auto text-[11.5px] text-muted">
            {stufe === 2 && mitDomain && !domainOk ? 'So geht es nicht weiter' : ''}
          </span>
          {ergebnis?.ok ? (
            <Button size="sm" onClick={schliessen}>
              Fertig
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={stufe === 0 || anlegen.isPending}
                onClick={() => {
                  anlegen.reset()
                  setStufe((s) => Math.max(0, s - 1))
                }}
              >
                Zurück
              </Button>
              {stufe < 3 ? (
                <Button size="sm" variant="primary" disabled={!stufeOk[stufe]} onClick={weiter}>
                  Weiter
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={anlegen.isPending || !stufeOk.every(Boolean)}
                  onClick={() => anlegen.mutate()}
                >
                  {anlegen.isPending
                    ? 'wird angelegt …'
                    : ergebnis
                      ? 'Noch einmal ausführen'
                      : 'Umgebung anlegen'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Anwendung aus dem Repo anlegen ────────────────────────────────────── */

const QUELLE_TEXT = {
  coolify: 'aus einer Coolify-Anwendung desselben Repos',
  gemerkt: 'gemerkt aus einer früheren Umgebung',
  dateien: 'aus den Dateien im Repo erkannt',
} as const

const BUILD_TEXT: Record<string, string> = {
  nixpacks: 'Nixpacks (erkennt Sprache selbst)',
  dockerfile: 'Dockerfile',
  dockercompose: 'Docker Compose',
  static: 'Statische Dateien',
}

/**
 * **Anwendung anlegen** – Work schlägt Build-Art und Port vor (Coolify › Gemerktes › Dateien) und
 * merkt sich, was benutzt wurde. Nils muss es nicht wissen; ändern kann er es trotzdem.
 */
function AnwendungDialog({
  umgebungId,
  label,
  schliessen,
}: {
  umgebungId: string
  label: string
  schliessen: () => void
}) {
  const vorschlag = useQuery({
    queryKey: ['umgebung', 'bauweise', umgebungId],
    queryFn: () => trpc.umgebung.bauweise.query({ umgebungId }),
    staleTime: 0,
    retry: false,
  })
  const [buildPack, setBuildPack] = useState<
    'nixpacks' | 'dockerfile' | 'dockercompose' | 'static' | null
  >(null)
  const [port, setPort] = useState('')
  const [statisch, setStatisch] = useState(false)
  const [publishDir, setPublishDir] = useState('')
  const [werte, setWerte] = useState<Record<string, string>>({})
  useEffect(() => {
    const v = vorschlag.data
    if (!v || buildPack) return
    setWerte(Object.fromEntries(v.variablen.map((x) => [x.key, x.wert])))
    setBuildPack(v.buildPack)
    setPort(v.port)
    setStatisch(v.statisch)
    setPublishDir(v.publishDir ?? '')
  }, [vorschlag.data, buildPack])

  const anlegen = useMutation({
    mutationFn: () =>
      trpc.umgebung.anwendungAnlegen.mutate({
        umgebungId,
        buildPack: buildPack ?? 'nixpacks',
        port: port.trim(),
        statisch,
        publishDir: publishDir.trim() || null,
        serverUuid: null,
        variablen: (vorschlag.data?.variablen ?? []).map((x) => ({
          key: x.key,
          value: werte[x.key] ?? '',
          erzeugen: x.art === 'erzeugt',
        })),
      }),
  })

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      if (!anlegen.isPending) schliessen()
    }
    window.addEventListener('keydown', taste, true)
    return () => window.removeEventListener('keydown', taste, true)
  }, [schliessen, anlegen.isPending])

  const ergebnis = anlegen.data
  const v = vorschlag.data

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[5vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen, !anlegen.isPending)}
      role="dialog"
      aria-modal="true"
      aria-label="Anwendung anlegen"
    >
      <div className="flex max-h-[min(88vh,100%)] w-full max-w-[620px] min-w-0 flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-start gap-2 border-b border-line px-[18px] py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold">Anwendung für „{label}“</h3>
            <p className="text-[11.5px] text-muted">
              Coolify baut aus dem Repo, rollt bei jedem Push aus und holt das Zertifikat für die
              Domain.
            </p>
          </div>
          <button
            type="button"
            onClick={schliessen}
            disabled={anlegen.isPending}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5 text-[13px]">
          {vorschlag.isPending && <p className="text-muted">Work sieht im Repo nach …</p>}
          {vorschlag.error && <p className="text-bad">✗ {vorschlag.error.message}</p>}
          {v && !ergebnis && (
            <div className="grid gap-3">
              <div
                className={`rounded-[10px] px-3 py-2 text-[12.5px] ${v.sicher ? CHIP.ok : CHIP.warn}`}
              >
                <b className="block">
                  {BUILD_TEXT[v.buildPack] ?? v.buildPack}, Port {v.port}
                  {v.statisch ? `, statisch aus ${v.publishDir ?? '/'}` : ''}
                </b>
                <span className="block text-[11.5px]">
                  {QUELLE_TEXT[v.quelle]}: {v.gruende.join(' ')}
                  {!v.sicher && ' Bitte prüfen.'}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-[12px] font-semibold text-ink2">Build</span>
                  <select
                    className="rounded-lg border border-line2 px-2 py-1.5"
                    value={buildPack ?? 'nixpacks'}
                    onChange={(e) => setBuildPack(e.target.value as typeof buildPack)}
                  >
                    {Object.entries(BUILD_TEXT).map(([k, t]) => (
                      <option key={k} value={k}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className="text-[12px] font-semibold text-ink2">Port</span>
                  <input
                    className="rounded-lg border border-line2 px-2 py-1.5 font-mono"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                  />
                </label>
                <label className="flex items-center gap-1.5 text-[12.5px]">
                  <input
                    type="checkbox"
                    checked={statisch}
                    onChange={(e) => setStatisch(e.target.checked)}
                  />
                  statisch ausliefern
                </label>
                {statisch && (
                  <label className="grid gap-1">
                    <span className="text-[12px] font-semibold text-ink2">Ausgabeordner</span>
                    <input
                      className="rounded-lg border border-line2 px-2 py-1.5 font-mono"
                      value={publishDir}
                      onChange={(e) => setPublishDir(e.target.value)}
                      placeholder="dist"
                    />
                  </label>
                )}
              </div>
              {v.variablen.length > 0 && (
                <div className="grid gap-1.5">
                  <span className="text-[12px] font-semibold text-ink2">
                    Umgebungsvariablen aus {v.variablenDatei}
                  </span>
                  <div className="max-h-[240px] overflow-y-auto rounded-[11px] border border-line">
                    {v.variablen.map((x) => (
                      <div
                        key={x.key}
                        className="flex items-center gap-2 border-t border-line px-2.5 py-1.5 first:border-t-0"
                      >
                        <span className="w-[42%] min-w-0 truncate font-mono text-[11.5px]">{x.key}</span>
                        {x.art === 'erzeugt' ? (
                          <span className="flex-1 text-[11.5px] text-good-text">wird erzeugt</span>
                        ) : (
                          <input
                            className={`min-w-0 flex-1 rounded-md border px-2 py-0.5 font-mono text-[11.5px] ${
                              x.art === 'fehlt' && !(werte[x.key] ?? '') ? 'border-bad' : 'border-line2'
                            }`}
                            type={/KEY|SECRET|TOKEN|PASSWORD|PASS$/i.test(x.key) ? 'password' : 'text'}
                            autoComplete="off"
                            value={werte[x.key] ?? ''}
                            placeholder={x.art === 'fehlt' ? 'fehlt' : ''}
                            onChange={(e) => setWerte({ ...werte, [x.key]: e.target.value })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <span className="text-[11px] text-muted">
                    Leer gelassene Werte werden nicht gesetzt. Die Werte gehen nur an Coolify – Work
                    speichert sie nicht.
                  </span>
                </div>
              )}
              <p className="rounded-lg bg-[#f7f7f5] px-3 py-2 text-[11.5px] text-ink2">
                Work legt an: Schlüssel in Coolify, Deploy-Key (nur lesen) im Repo, die Anwendung
                mit Domain, einen Webhook „bei Push ausrollen“ – und rollt einmal aus. Was du hier
                einstellst, merkt sich Work für die nächste Umgebung aus diesem Repo.
              </p>
            </div>
          )}
          {anlegen.error && <p className="mt-2 text-[12px] text-bad">✗ {anlegen.error.message}</p>}
          {ergebnis && (
            <div className="grid gap-3">
              <div className="overflow-hidden rounded-[11px] border border-line">
                {ergebnis.schritte.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 border-t border-line px-3 py-2 first:border-t-0"
                  >
                    <span className={s.ergebnis === 'fehler' ? 'text-bad' : 'text-good-text'}>
                      {s.ergebnis === 'fehler' ? '✕' : '✓'}
                    </span>
                    <span className="min-w-0 flex-1 break-words">
                      {s.was}
                      {s.text && <span className="block text-[11.5px] text-muted">{s.text}</span>}
                    </span>
                    <span className="text-[11px] text-muted">{s.ergebnis}</span>
                  </div>
                ))}
              </div>
              <p className={`text-[12.5px] ${ergebnis.ok ? 'text-good-text' : 'text-bad'}`}>
                {ergebnis.ok
                  ? 'Angelegt. Den Stand des Ausrollens zeigt die Umgebung, sobald Coolify fertig ist.'
                  : 'Abgebrochen. Was schon angelegt ist, bleibt – nach dem Beheben noch einmal ausführen.'}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-[18px] py-3">
          {ergebnis?.ok ? (
            <Button size="sm" onClick={schliessen}>
              Fertig
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              disabled={!v || anlegen.isPending || !/^\d{2,5}(,\d{2,5})*$/.test(port.trim())}
              onClick={() => anlegen.mutate()}
            >
              {anlegen.isPending
                ? 'wird angelegt …'
                : ergebnis
                  ? 'Noch einmal ausführen'
                  : 'Anwendung anlegen'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * **`git clone …` zum Kopieren** – damit Entwickler nach dem Anlegen sofort loslegen können
 * (Nils: „dann müssen die Entwickler das nicht tun und könnten dann mit dem Klonen arbeiten").
 */
function KlonZeile({ repoUrl, pfad }: { repoUrl: string; pfad: string }) {
  const [kopiert, setKopiert] = useState(false)
  let befehl = ''
  try {
    befehl = `git clone git@${new URL(repoUrl).hostname}:${pfad}.git`
  } catch {
    return null
  }
  return (
    <span className="mt-1 flex items-center gap-1.5">
      <code className="min-w-0 truncate rounded bg-[#f3f2ef] px-1.5 py-px text-[11px]">
        {befehl}
      </code>
      <button
        type="button"
        className="shrink-0 cursor-pointer text-[11px] text-accent-deep hover:underline"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(befehl)
            .then(() => {
              setKopiert(true)
              setTimeout(() => setKopiert(false), 1500)
            })
            .catch(() => undefined)
        }}
      >
        {kopiert ? 'kopiert' : 'kopieren'}
      </button>
    </span>
  )
}
